import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Schema } from '../models/schema.model';
import { Field } from '../models/field.model';
import { SchemaScript } from '../models/schema-script.model';
import { SchemaProperty } from '../models/schema-property.model';

export class SchemaService {
  public readonly schemaBaseUrl: string;

  constructor(private readonly httpClient: HttpClient, private baseUrl: string, public readonly schemaName: string) {
    this.schemaBaseUrl = `${this.baseUrl}/schemas/${this.schemaName}`;
  }

  getSchemaDefinition(): Observable<Schema> {
    const url = `${this.schemaBaseUrl}`;
    return this.httpClient.get(url) as Observable<Schema>;
  }

  deleteSchema(): Observable<Schema> {
    const url = `${this.schemaBaseUrl}`;
    return this.httpClient.delete(url) as Observable<Schema>;
  }

  addField(field: Field): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields`;
    return this.httpClient.post(url, field) as Observable<Schema>;
  }

  //Add field to array field
  addNestedField(parentId: number, fields: Field): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentId}/nested`;
    return this.httpClient.post(url, fields) as Observable<Schema>;
  }

  updateField(field: Field, fieldId?: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${(fieldId ? fieldId : field.fieldId)}`;
    return this.httpClient.put(url, field) as Observable<Schema>;
  }

  updateNestedField(nestedField: Field, parentFieldId: string, nestedFieldId?: string): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${nestedFieldId ?
      nestedFieldId : nestedField.fieldId}`;
    return this.httpClient.put(url, nestedField) as Observable<Schema>;
  }

  reorderFields(fieldIds: number[]): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/ordering`;
    return this.httpClient.put(url, { 'fieldIds': fieldIds }) as Observable<Schema>;
  }

  reorderNestedFields(parentId: number, fieldIds: number[]): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentId}/nested/ordering`;
    return this.httpClient.put(url, { 'fieldIds': fieldIds }) as Observable<Schema>;
  }

  deleteField(fieldId: number, force: boolean = false): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${fieldId}`;
    return this.httpClient.delete(url) as Observable<Schema>;
  }

  deleteNestedField(parentFieldId: number, fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${fieldId}`;
    return this.httpClient.delete(url) as Observable<Schema>;
  }

  lockField(fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${fieldId}/lock`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  lockNestedField(parentFieldId: number, fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${fieldId}/lock`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  hideField(fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${fieldId}/hide`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  hideNestedField(parentFieldId: number, fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${fieldId}/hide`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }


  showField(fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${fieldId}/show`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  showNestedField(parentFieldId: number, fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${fieldId}/show`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  enableField(fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${fieldId}/enable`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  enableNestedField(parentFieldId: number, fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${fieldId}/enable`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  disableField(fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${fieldId}/desable`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  disableNestedField(parentFieldId: number, fieldId: number): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/fields/${parentFieldId}/nested/${fieldId}/desable`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  updateSchemaProperties(schemaProperty: SchemaProperty): Observable<Schema> {
    const url = `${this.schemaBaseUrl}`;
    return this.httpClient.put(url, schemaProperty) as Observable<Schema>;
  }

  synchronizeSchema(updatedSchema: Schema): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/sync`;
    return this.httpClient.put(url, updatedSchema) as Observable<Schema>;
  }

  updateSchemaCategory(categoryName: string): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/category`;
    return this.httpClient.put(url, { name: categoryName }) as Observable<Schema>;
  }

  updatePreviewsUrl(PreviewsUrl: { [id: string]: string }): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/preview-urls`;
    return this.httpClient.put(url, PreviewsUrl) as Observable<Schema>;
  }

  updateScripts(scripts: SchemaScript): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/scripts`;
    return this.httpClient.put(url, scripts) as Observable<Schema>;
  }

  publishSchema(): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/publish`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }

  unPublishSchema(): Observable<Schema> {
    const url = `${this.schemaBaseUrl}/unpublish`;
    return this.httpClient.put(url, {}) as Observable<Schema>;
  }
}
